import { OptionType } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { useMemo, useState } from 'react';
import { useSpaces } from './useSpaces';
import { OwningSpaceIndicator } from '../../components/OwningSpaceIndicator';
import { SpaceActionType } from '@fullstory/library-ui/types';

/**
 * Helper for fetching and formulating a list of Space options.
 *
 * @param objectOwningSpaceId The object owning space for promotion to the Current Space (disabled) option at the top.
 * @param enabled turns off the fetch for conditional use.
 */
export const useSpaceOptions = (
  objectOwningSpaceId: string | null | undefined,
  mode: SpaceActionType,
  enabled: boolean = true,
  featuringEnabled: boolean = true,
  disableOwningSpaceOption: boolean = true,
) => {
  const [selected, setSelected] = useState<OptionType<string> | null>(null);
  const { spaces } = useSpaces(enabled);

  const disableOwningOption =
    disableOwningSpaceOption && (mode === 'transfer' || (mode === 'add' && !featuringEnabled));

  const options: OptionType<string>[] = useMemo(() => {
    const { owningSpaceOption, availableSpaceOptions } = spaces.reduce<{
      owningSpaceOption: OptionType<string> | undefined;
      availableSpaceOptions: OptionType<string>[];
    }>(
      (acc, s) => {
        const option: OptionType<string> = {
          label: s.name,
          key: s.id,
          value: s.id,
        };

        if (s.id === objectOwningSpaceId && disableOwningOption) {
          acc.owningSpaceOption = {
            ...option,
            group: intl.formatMessage({ defaultMessage: 'Current Space', id: 'j2m5zG' }),
            disabled: true,
            icon: 'checkmark',
          };
        } else if (s.id === objectOwningSpaceId) {
          acc.owningSpaceOption = {
            ...option,
            group: intl.formatMessage({ defaultMessage: 'Current Space', id: 'j2m5zG' }),
            rightAdornment: mode !== 'save' ? OwningSpaceIndicator : undefined,
          };
        } else {
          acc.availableSpaceOptions.push({
            ...option,
            group: intl.formatMessage({ defaultMessage: 'Available Spaces', id: 'XWJHUb' }),
          });
        }

        return acc;
      },
      { owningSpaceOption: undefined, availableSpaceOptions: [] },
    );

    const options = owningSpaceOption ? [owningSpaceOption] : [];
    return options.concat(availableSpaceOptions);
  }, [disableOwningOption, mode, objectOwningSpaceId, spaces]);

  const selectedSpace = useMemo(
    () => spaces.find(s => s.id === selected?.value),
    [selected?.value, spaces],
  );

  return {
    spaces,
    options,
    selected,
    selectedSpace,
    setSelected,
  };
};
